import { supabase } from './supabase';
import { db } from './db';
import { newId } from './ids';
import { queue } from './sync';
import { prepareFile, storagePath } from './images';
import type { Attachment, AttachmentEntity } from '@/types/models';

const BUCKET = 'evidence';

/** Signed URLs expire, so they are cached only for as long as they are
 *  valid — long enough to scroll a register, short enough that a link
 *  copied out of the page stops working. */
const URL_TTL_SECONDS = 60 * 60;
const urlCache = new Map<string, { url: string; expires: number }>();

/**
 * Capture a file against a record.
 *
 * The row is written to the local mirror immediately and queued, so the
 * photograph appears in the drawer the moment it is taken — at the face,
 * with no signal, that is the only feedback the supervisor gets that the
 * camera worked.
 */
export async function addEvidence(
  entityType: AttachmentEntity,
  entityId: string,
  file: File,
  caption?: string,
  siteId?: string,
): Promise<Attachment> {
  const prepared = await prepareFile(file);
  const id = newId();
  const path = storagePath(entityType, entityId, id, prepared.mime);
  const now = new Date().toISOString();

  await db.evidence_blobs.put({
    id,
    blob: prepared.blob,
    mime: prepared.mime,
    storage_path: path,
  });

  const row: Attachment = {
    id,
    // The record's site (a branch's fill); the server's default otherwise.
    ...(siteId ? { site_id: siteId } : {}),
    entity_type: entityType,
    entity_id: entityId,
    storage_path: path,
    file_name: prepared.fileName,
    mime_type: prepared.mime,
    bytes: prepared.blob.size,
    width: prepared.width,
    height: prepared.height,
    caption: caption ?? null,
    taken_at: now,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  } as Attachment;

  await db.attachments.put(row);
  await queue({ entity: 'attachment', entity_id: id, op: 'upsert', payload: row });
  return row;
}

/** Soft delete, like every other record — evidence is not shredded, it is
 *  marked withdrawn, and the bytes stay for the audit trail. */
export async function removeEvidence(id: string): Promise<void> {
  const row = await db.attachments.get(id);
  if (!row) return;
  const next = { ...row, deleted_at: new Date().toISOString() };
  await db.attachments.put(next);
  await db.evidence_blobs.delete(id);
  await queue({ entity: 'attachment', entity_id: id, op: 'upsert', payload: next });
}

export async function captionEvidence(id: string, caption: string): Promise<void> {
  const row = await db.attachments.get(id);
  if (!row) return;
  const next = { ...row, caption: caption || null, updated_at: new Date().toISOString() };
  await db.attachments.put(next);
  await queue({ entity: 'attachment', entity_id: id, op: 'upsert', payload: next });
}

/**
 * Something to display for one attachment.
 *
 * A file still waiting for a signal is shown from the blob on the device;
 * anything already uploaded needs a signed URL, because the bucket is
 * private. Offline and already synced is the one case with nothing to show,
 * and the component says so rather than rendering a broken image.
 */
export async function evidenceSrc(row: Attachment): Promise<string | null> {
  const pending = await db.evidence_blobs.get(row.id);
  if (pending) return URL.createObjectURL(pending.blob);

  const cached = urlCache.get(row.storage_path);
  if (cached && cached.expires > Date.now()) return cached.url;

  if (!navigator.onLine) return null;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(row.storage_path, URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;

  urlCache.set(row.storage_path, {
    url: data.signedUrl,
    // Expire the cache entry a minute early so a link is never handed out
    // in the second it dies.
    expires: Date.now() + (URL_TTL_SECONDS - 60) * 1000,
  });
  return data.signedUrl;
}

/** True while the bytes are still on this device only. */
export async function isPending(id: string): Promise<boolean> {
  return (await db.evidence_blobs.get(id)) !== undefined;
}

/**
 * Throw away evidence captured against a record that was never saved.
 *
 * Attachments point at seven different tables, so `entity_id` cannot be a
 * foreign key and nothing stops a photograph outliving the record it was
 * taken for. A supervisor who scans a ticket and then closes the drawer
 * without saving would otherwise leave a scan of nothing in the register.
 *
 * Anything still queued is dropped outright — it was never anywhere else.
 * Anything already sent is withdrawn the normal way, because the server has
 * it and the audit trail should show that it went and came back.
 */
export async function discardEvidenceFor(
  entityType: AttachmentEntity,
  entityId: string,
): Promise<void> {
  const rows = await db.attachments
    .where('[entity_type+entity_id]')
    .equals([entityType, entityId])
    .toArray();

  for (const row of rows) {
    const queued = await db.outbox
      .where('entity_id').equals(row.id)
      .and((e) => e.entity === 'attachment')
      .toArray();

    if (queued.length > 0) {
      await db.outbox.bulkDelete(queued.map((e) => e.seq!).filter(Boolean));
      await db.attachments.delete(row.id);
      await db.evidence_blobs.delete(row.id);
    } else {
      await removeEvidence(row.id);
    }
  }
}
